import { z } from 'zod';
import { DAYS_MAX } from '../limits.js';
import { IsoTimestampSchema, SchemaVersionSchema } from './shared.js';
export const AlertRecipientSchema = z.enum(['rep', 'opsAdmin', 'superAdmin']);
export const AlertRuleSchema = z.object({
    // QA 2026-10-01 F-08 — same ten-year ceiling as PUT /admin/alerting-config.
    thresholdDays: z.number().int().positive().max(DAYS_MAX),
    notify: z.array(AlertRecipientSchema),
    escalateAfterDays: z.number().int().positive().max(DAYS_MAX).optional(),
    escalateTo: z.array(AlertRecipientSchema).optional(),
});
export const AlertingConfigSchema = z.object({
    id: z.literal('alerting'),
    type: z.literal('alerting_config'),
    schemaVersion: SchemaVersionSchema,
    rules: z.object({
        staleLead: AlertRuleSchema,
        staleEngaged: AlertRuleSchema,
        staleProposalPending: AlertRuleSchema,
        staleSignaturePending: AlertRuleSchema,
        staleIntakeSubmitted: AlertRuleSchema,
        staleActiveCheckIn: AlertRuleSchema,
        invoiceOverdue: AlertRuleSchema,
        // RIG-501 — "No Contact Yet". An account with zero live contacts is
        // nobody's to call, and until now nothing chased it.
        //
        // `.default()` is load-bearing, not tidiness. The deployed
        // `alerting_config` row predates this rule, so a REQUIRED key here would
        // make `AlertingConfigSchema.safeParse` fail on that row, and
        // `stale-record-alerts` aborts the whole run on a parse failure. Adding
        // the eighth rule would have silently killed the other seven. Same
        // reasoning as `submissionExpiryDays` below.
        //
        // 7 days, not 1: a brand new account gets a week to find a name before
        // anyone is nagged. `notify` is rep + opsAdmin together because the ask is
        // a single weekly nudge to the owner and to ops. Escalation
        // (`escalateAfterDays` + `escalateTo`) is optional here exactly as it is
        // on every other rule (AlertRuleSchema), and off by default.
        noContactYet: AlertRuleSchema.default({
            thresholdDays: 7,
            notify: ['rep', 'opsAdmin'],
        }),
    }),
    // T5-013 — Submission-expiry cron tunables. Defaults match the Phase 5
    // spec line 44 ("Submission expiry cron fires at 60d default") and the
    // 3-day reminder window. Both fields are .default()'d so the existing
    // `alerting_config` DB row (which predates T5-013) validates without
    // a backfill — the schema's parse step supplies the defaults when the
    // configRow object passed in lacks these fields. Future admin-UI edits
    // can override per-tenant by writing real values back to the row;
    // T5-013 itself only consumes them via the parsed result.
    submissionExpiryDays: z.number().int().positive().max(DAYS_MAX).default(60),
    submissionExpiryReminderDays: z.number().int().positive().max(DAYS_MAX).default(3),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    version: z.number().int().positive(),
});
//# sourceMappingURL=alerting-config.js.map